import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import {
    afterRenderEffect,
    Directive,
    ElementRef,
    inject,
    input,
    numberAttribute,
} from '@angular/core';

@Directive({
    selector: 'textarea[qpxTextareaAutosize]',
    host: {
        '[class.qpx-textarea-native]': 'nativeSizing',
        '[style.overflow-y]': '"auto"',
        '[style.resize]': '"none"',
    },
    hostDirectives: [
        {
            directive: CdkTextareaAutosize,
            inputs: [
                'cdkAutosizeMinRows: minRows',
                'cdkAutosizeMaxRows: maxRows',
                'placeholder',
            ],
        },
    ],
})
export class QpxTextareaAutosize {
    //#region Dependency injection
    private readonly fallback = inject(CdkTextareaAutosize);
    private readonly elementRef =
        inject<ElementRef<HTMLTextAreaElement>>(ElementRef);
    //#endregion

    //#region Constants
    protected readonly nativeSizing =
        typeof CSS !== 'undefined' &&
        typeof CSS.supports === 'function' &&
        CSS.supports('field-sizing', 'content');
    //#endregion

    //#region Inputs
    public readonly minRows = input(1, { transform: numberAttribute });
    public readonly maxRows = input(0, { transform: numberAttribute });
    //#endregion

    constructor() {
        this.fallback.enabled = !this.nativeSizing;
        if (!this.nativeSizing) {
            this.fallback.minRows = this.minRows();
            this.fallback.maxRows = this.maxRows();
        }
        afterRenderEffect({
            write: () => this.updateStyles(),
            mixedReadWrite: () => {
                if (this.nativeSizing) return;
                const minRows = this.minRows();
                const maxRows = this.maxRows();
                if (!(minRows > 0) || !(maxRows > 0)) {
                    this.fallback.resizeToFitContent(true);
                }
            },
        });
    }

    //#region Private methods
    private updateStyles(): void {
        const style = this.elementRef.nativeElement.style;
        if (this.nativeSizing) {
            style.setProperty('field-sizing', 'content');
            style.height = 'auto';
            style.minHeight = this.minRows() > 0 ? `${this.minRows()}lh` : '';
            style.maxHeight = this.maxRows() > 0 ? `${this.maxRows()}lh` : '';
            style.boxSizing = 'content-box';
            return;
        }

        if (!(this.minRows() > 0)) {
            style.removeProperty('min-height');
        }
        if (!(this.maxRows() > 0)) {
            style.removeProperty('max-height');
        }
    }
    //#endregion
}
